/**
 * GUESTS tab — README §1: WHO'S IN capped at 10 tiles (64px square avatar + name + note) then
 * VIEW ALL N GUESTS → the guest list modal; FRIENDS GOING panel. Visibility follows the
 * server rule for GET /events/{id}/guest-list (host / co-host, or an application in
 * accepted · paid · external_interest): logged-out reads SIGN IN TO SEE WHO, an ineligible
 * viewer gets the honest empty state.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Panel, PanelHeader, PanelFooterButton, EmptyBox, SurfaceButton, SwitchRow, MUTED, SUBTLE, SOFT, GREEN } from '../surface';
import { RoundAvatar } from './EventHeader';

const CAP = 10;

/** A guest as a #F5F5F5 pill: 34px round avatar + name (+ YOU / FRIEND). */
function GuestTile({ entry, friend, mobile }) {
  // The host, co-hosts and staff get opted-out guests by name (the server tags them anonymous
  // but keeps the identity); everyone else sees them as a count. A guest-checkout stub is a
  // name only — no page, no friend request.
  const name = entry.isAnonymous && !entry.displayName ? 'Anonymous' : (entry.displayName || '?');
  const note = entry.isViewer ? 'YOU' : friend ? 'FRIEND' : '';
  const avatar = entry.isAnonymous && !entry.displayName
    ? <span className="flex flex-shrink-0 items-center justify-center rounded-full font-mono font-bold" style={{ width: 34, height: 34, background: SOFT.listed, color: MUTED, fontSize: 14 }}>?</span>
    : <RoundAvatar person={{ displayName: name, avatarUrl: entry.avatarUrl, handle: entry.handle, userId: entry.userId }} size={34} />;
  const body = (
    <>
      {avatar}
      <span className="truncate font-mono font-bold" style={{ fontSize: mobile ? 11 : 12, color: entry.isAnonymous && !entry.displayName ? MUTED : undefined }}>{name}</span>
      {entry.isAnonymous && entry.displayName && <span className="font-mono font-bold uppercase" style={{ fontSize: 8, letterSpacing: '0.14em', color: MUTED }}>HIDDEN</span>}
      {note && <span className="font-mono font-bold uppercase" style={{ fontSize: 8, letterSpacing: '0.14em', color: note === 'FRIEND' ? GREEN : MUTED }}>{note}</span>}
    </>
  );
  const cls = 'flex max-w-full items-center gap-[10px] rounded-full';
  const style = { padding: '6px 16px 6px 6px', background: SOFT.canvas };
  // `/{handle}`, or `/users/{userId}` for an account that never picked a handle — every
  // guest with an account opens their Portal Page (Oct 20 2026).
  const to = entry.isStub || (entry.isAnonymous && !entry.displayName) ? null : entry.handle ? `/${entry.handle}` : entry.userId ? `/users/${entry.userId}` : null;
  return to
    ? <Link to={to} aria-label={`${name} — view Portal Page`} className={cls} style={style} data-testid="guest-tile">{body}</Link>
    : <div className={cls} style={style} data-testid="guest-tile">{body}</div>;
}

export default function EventGuestsTab({
  isAuthenticated,
  canView,
  guestList,
  loaded,
  friendIds,
  onOpenAll,
  onToggleVisibility,
  saving = false,
  mobile = false,
  onSignIn,
}) {
  if (!isAuthenticated) {
    return (
      <EmptyBox title="SIGN IN TO SEE WHO" mobile={mobile} testId="guests-signin" action={<SurfaceButton variant="fill" size="sm" onClick={onSignIn}>SIGN IN</SurfaceButton>}>
        The guest list opens up to people with a ticket.
      </EmptyBox>
    );
  }
  if (!canView) {
    return (
      <EmptyBox title="GUESTS ONLY." mobile={mobile} testId="guests-gated">
        Who's in shows up once you hold a ticket for this one.
      </EmptyBox>
    );
  }
  if (!loaded) {
    return <div className="bg-white font-mono" style={{ padding: mobile ? '18px 16px' : 26, fontSize: 12, color: MUTED }} data-testid="guests-loading">Loading the list…</div>;
  }
  const entries = guestList?.entries || [];
  const total = (guestList?.shownCount || 0) + (guestList?.anonymousCount || 0);
  const friends = entries.filter((g) => g.userId && !g.isViewer && friendIds?.has?.(g.userId));
  const shown = entries.slice(0, CAP);

  const toggle = guestList?.viewerShown != null && (
    <SwitchRow label="Show me on the guest list" desc="Off keeps you as Anonymous to the other guests." checked={!!guestList.viewerShown} onChange={onToggleVisibility} disabled={saving} dense testId="guest-visibility" />
  );

  if (entries.length === 0) {
    return (
      <div className="flex flex-col gap-3">
        {toggle && <Panel>{toggle}</Panel>}
        <EmptyBox title="NOBODY ON THE LIST YET." mobile={mobile} testId="guests-empty">Be the first name on it.</EmptyBox>
      </div>
    );
  }

  const friendsLine = friends.length === 0
    ? 'Nobody you know is on this list yet.'
    : friends.length === 1
      ? `${friends[0].displayName} is on this list.`
      : friends.length === 2
        ? `${friends[0].displayName} and ${friends[1].displayName} are on this list.`
        : `${friends[0].displayName}, ${friends[1].displayName} and ${friends.length - 2} ${friends.length - 2 === 1 ? 'other' : 'others'} you know are on this list.`;

  return (
    <div className="flex flex-col" style={{ gap: mobile ? 10 : 14 }} data-testid="guests-tab">
      <Panel dense={mobile} data-testid="whos-in">
        <PanelHeader
          label="GUESTS"
          dense={mobile}
          right={(
            <span className="flex items-center gap-2 font-mono font-bold uppercase" style={{ fontSize: 9, letterSpacing: '0.14em', color: SUBTLE }}>
              {friends.length > 0 && <span style={{ fontSize: 10, color: GREEN }}>{friends.length} {friends.length === 1 ? 'FRIEND' : 'FRIENDS'} IN</span>}
              <span>{total} GOING</span>
            </span>
          )}
        />
        {toggle}
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          {shown.map((g, i) => <GuestTile key={g.userId || `anon-${i}`} entry={g} friend={g.userId ? friendIds?.has?.(g.userId) : false} mobile={mobile} />)}
        </div>
        <span className="font-mono" style={{ fontSize: 11, color: MUTED }}>Only people who chose to be shown appear by name. Everyone else is a count.</span>
        <PanelFooterButton onClick={onOpenAll} data-testid="view-all-guests">VIEW ALL {total} GUESTS</PanelFooterButton>
      </Panel>
      <Panel dense={mobile} data-testid="friends-going">
        <PanelHeader label="FRIENDS GOING" right={friends.length > 0 ? `${friends.length} ${friends.length === 1 ? 'FRIEND' : 'FRIENDS'}` : undefined} dense={mobile} />
        <div className="font-mono" style={{ fontSize: 13, lineHeight: 1.6 }}>
          {friendsLine}{' '}
          {friends.length > 0 && <button type="button" onClick={onOpenAll} className="underline hover:no-underline">See who</button>}
        </div>
      </Panel>
    </div>
  );
}
